import React, { FC } from 'react';
import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { PaymentCard } from 'src/store/subscriptionStore';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { formatMaskedCardNumber, getCardBrandDisplayName } from 'src/utils/cardUtils';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/close-icon.svg';
import './SubscriptionModals.scss';

interface SelectPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  cards: PaymentCard[];
  isUpdatingCardId?: string | null;
  onSelectCard: (cardId: string) => void;
  onAddNewCard: () => void;
}

export const SelectPaymentMethodModal: FC<SelectPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  cards,
  isUpdatingCardId,
  onSelectCard,
  onAddNewCard,
}) => {
  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      closeIcon={null}
      rootClassName="subscription-modal-wrapper"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.75)',
        },
      }}
    >
      <div className="select-payment-method-modal-content">
        <div className="modal-header-with-close">
          <div className="modal-header">
            <h2>{'Payment Methods'}</h2>
            <p>{'Select a payment method to set it as default for your subscription.'}</p>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="modal-cards-list">
          {cards.map((card) => {
            const isThisUpdating = isUpdatingCardId === card.id;
            const isBusy = Boolean(isUpdatingCardId);
            return (
              <div
                key={card.id}
                className={`modal-card-item ${card.isDefault ? 'selected' : ''} ${isThisUpdating ? 'is-loading' : ''} ${isBusy ? 'disabled' : ''}`}
                onClick={() => {
                  if (isBusy) return;
                  onSelectCard(card.id);
                  onClose();
                }}
              >
                <div className="card-brand-wrap">
                  <CardBrandLogo brand={card.brand} />
                </div>
                <div className="card-info-text">
                  <span className="card-title-text">{getCardBrandDisplayName(card.brand)}</span>
                  <span className="card-mask">
                    {formatMaskedCardNumber(card.brand, card.last4)}
                  </span>
                  {card.expiry && <span className="card-exp">{`Expires ${card.expiry}`}</span>}
                </div>
                <div className="card-status-wrap">
                  {isThisUpdating ? (
                    <span className="setting-default-indicator">
                      <Spin
                        indicator={
                          <LoadingOutlined style={{ fontSize: 13, color: '#ffa100' }} spin />
                        }
                      />
                      <span>{'Updating...'}</span>
                    </span>
                  ) : card.isDefault ? (
                    <span className="default-pill">{'Default'}</span>
                  ) : (
                    <span className="set-default-action">{'Set as default'}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-bottom-actions">
          <button
            type="button"
            className="btn-add-new-card"
            onClick={() => {
              onClose();
              onAddNewCard();
            }}
          >
            {'+ Add new payment method'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
